import Analytics from '@/utils/analytics';
import ArrowLeftThin from '@assets/icons/arrow-left-thin.svg';
import ArrowRightThin from '@assets/icons/arrow-right-thin.svg';
import ChevronLeftIcon from '@assets/icons/chevron-left.svg';
import ChevronRightIcon from '@assets/icons/chevron-right.svg';
import React, { useRef, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { FadingComponent } from './fading-component';
import {
  FlatList,
  FlatListProps,
  Platform,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { shadow } from '@/styles';

interface Props<T extends {}> {
  arrowShape?: 'round' | 'square';
  arrowStyle?: StyleProp<ViewStyle>;
  children: (
    listViewProps: Partial<FlatListProps<T>>,
    ref: React.RefObject<FlatList<T> | null>,
  ) => React.ReactNode;
  displayAsStatic?: boolean;
  focusable?: boolean;
  staticArrowContainerStyle?: StyleProp<ViewStyle>;
  trackingKey: string;
}

const LIST_MANUAL_SCROLL_DISTANCE = 200;

export const HorizontalListArrows = <T extends {}>({
  arrowStyle,
  arrowShape = 'square',
  children,
  displayAsStatic = false,
  focusable = false,
  staticArrowContainerStyle,
  trackingKey,
}: Props<T>) => {
  const [styles, theme] = useStyles();

  const listRef = useRef<FlatList<T>>(null);
  const [contentWidth, setContentWidth] = useState(0);
  const [layoutWidth, setLayoutWidth] = useState(0);
  const [scrollOffset, setScrollOffset] = useState(0);

  const showArrows =
    Platform.OS === 'web' &&
    contentWidth &&
    layoutWidth &&
    contentWidth > layoutWidth;

  const listProps: Partial<FlatListProps<T>> & {
    ref: React.RefObject<FlatList<T> | null>;
  } = {
    onContentSizeChange: setContentWidth,
    onLayout: (e) => {
      setLayoutWidth(e.nativeEvent.layout.width);
    },
    onScroll: (e) => {
      setScrollOffset(e.nativeEvent.contentOffset.x);
    },
    ref: listRef,
  };

  const baseTouchableStyle =
    arrowShape === 'square'
      ? styles.arrowTouchable
      : displayAsStatic
        ? styles.staticArrowTouchableRound
        : styles.arrowTouchableRound;
  const touchableStyle = [baseTouchableStyle, arrowStyle];
  const arrowFill =
    arrowShape === 'square' ? theme.colors.white : theme.colors.primary;
  const buttonStyle = arrowShape === 'square' ? styles.arrowButtonSquare : null;
  const showStaticArrows = displayAsStatic && arrowShape === 'round';
  const staticArrowStyle = showStaticArrows ? styles.staticArrow : null;

  const arrows = (
    <>
      <FadingComponent
        isVisible={displayAsStatic || scrollOffset > 0}
        style={[
          styles.arrowButton,
          buttonStyle,
          styles.arrowButtonLeft,
          staticArrowStyle,
        ]}
      >
        <TouchableOpacity
          activeOpacity={1}
          focusable={focusable}
          onPress={() => {
            Analytics.track(`${trackingKey}.left_button.press`);
            listRef.current?.scrollToOffset({
              animated: true,
              offset: scrollOffset - LIST_MANUAL_SCROLL_DISTANCE,
            });
          }}
          style={touchableStyle}
        >
          {displayAsStatic && arrowShape === 'round' ? (
            <ArrowLeftThin fill={arrowFill} />
          ) : (
            <ChevronLeftIcon fill={arrowFill} />
          )}
        </TouchableOpacity>
      </FadingComponent>
      <FadingComponent
        isVisible={
          displayAsStatic ||
          Math.floor(contentWidth - layoutWidth - scrollOffset) > 0
        }
        style={[
          styles.arrowButton,
          buttonStyle,
          styles.arrowButtonRight,
          staticArrowStyle,
        ]}
      >
        <TouchableOpacity
          activeOpacity={1}
          focusable={focusable}
          onPress={() => {
            Analytics.track(`${trackingKey}.right_button.press`);
            listRef.current?.scrollToOffset({
              animated: true,
              offset: scrollOffset + LIST_MANUAL_SCROLL_DISTANCE,
            });
          }}
          style={touchableStyle}
        >
          {displayAsStatic && arrowShape === 'round' ? (
            <ArrowRightThin fill={arrowFill} />
          ) : (
            <ChevronRightIcon fill={arrowFill} />
          )}
        </TouchableOpacity>
      </FadingComponent>
    </>
  );

  return (
    <>
      {children(listProps, listRef)}
      {showArrows ? (
        <>
          {displayAsStatic ? (
            <View
              style={[styles.staticArrowsWrapper, staticArrowContainerStyle]}
            >
              {arrows}
            </View>
          ) : (
            <>{arrows}</>
          )}
        </>
      ) : null}
    </>
  );
};

const ROUND_BUTTON_DIMENSION = 40;
const ROUND_STATIC_BUTTON_DIMENSION = 32;

const useStyles = createStyles((theme) => ({
  arrowButton: {
    alignItems: 'center',
    bottom: 0,
    justifyContent: 'center',
    position: 'absolute',
    top: 0,
    width: 52,
  },
  arrowButtonLeft: {
    left: 0,
  },
  arrowButtonRight: {
    right: 0,
  },
  arrowButtonSquare: {
    width: 28,
  },
  arrowTouchable: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    backgroundColor: theme.colors.primaryOverlay,
    justifyContent: 'center',
  },
  arrowTouchableRound: {
    ...shadow({
      color: theme.colors.black,
      elevation: 2,
      opacity: 0.2,
      radius: 4,
      x: 0,
      y: 0,
    }),
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    borderRadius: ROUND_BUTTON_DIMENSION,
    height: ROUND_BUTTON_DIMENSION,
    justifyContent: 'center',
    width: ROUND_BUTTON_DIMENSION,
  },
  staticArrow: {
    position: 'relative',
    width: ROUND_STATIC_BUTTON_DIMENSION,
  },
  staticArrowsWrapper: {
    flexDirection: 'row',
    gap: 16,
    justifyContent: 'flex-end',
    paddingHorizontal: 16,
  },
  staticArrowTouchableRound: {
    ...shadow({
      color: theme.colors.black,
      elevation: 2,
      opacity: 0.2,
      radius: 4,
      x: 0,
      y: 0,
    }),
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    borderRadius: ROUND_STATIC_BUTTON_DIMENSION,
    height: ROUND_STATIC_BUTTON_DIMENSION,
    justifyContent: 'center',
    width: ROUND_STATIC_BUTTON_DIMENSION,
  },
}));
